import React, { useState, useEffect } from 'react'
import withSSR from 'js/components/withSSR/withSSR'
import Spinner from 'js/components/Spinner/Spinner'
import ErrorPage from 'js/pages/ErrorPage'
import DatoImage from '../../components/DatoRichText/DatoImage'
import convert from 'htmr'
import { runQuery } from 'js/dato'
import { query } from 'js/pages/NutritionExplained/queries/nutritionRecipeQuery'
import useLocale from '../../hooks/useLocale'
import NutritionFooter from './components/NutritionFooter'
import NutritionHeader from './components/NutritionHeader'
import RecipeMeta from './components/RecipeMeta'
import SymbolHeading from './components/SymbolHeading'
import SaucePanHot from './assets/sauce-pan-hot.svg?svgr-webpack'
import AppPromo from './components/AppPromo'
import RelatedStories from './components/RelatedStories'
import { Link, useParams, useLocation } from 'react-router-dom'
import DatoMeta from '../../components/DatoMeta'
import ShareButtons from '../../components/ShareButtons'
import { FormattedMessage } from 'react-intl'
import Logo from 'js/components/Logo'
import datoLocales from 'js/datoLocales'
import {
  NutritionRecipe_nutritionRecipe,
  NutritionRecipe_nutritionRecipe_categories,
} from './queries/types/NutritionRecipe'

type CategoriesProps = {
  categories: NutritionRecipe_nutritionRecipe_categories[]
}

const Categories = ({ categories }: CategoriesProps) => {
  if (!categories.length) return null

  return (
    <div className="mt-40 lg:mt-64">
      <h2 className="type-upper-fat mb-16 lg:mb-24">Categories</h2>
      <ul className="flex flex-wrap">
        {categories.map(({ name, slug, id }) => (
          <li key={id} className="mb-12 mr-12 lg:mb-16 lg:mr-16">
            <Link
              to={`/nutrition-explained/category/${slug}`}
              className={`
                type-upper rounded-full border-1
                border-BrandDark px-16 pb-4
                pt-6 text-BrandDark
                transition
                hover:bg-green-lightest
              `}
            >
              {name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

type Props = {
  data: NutritionRecipe_nutritionRecipe
  isLoading: boolean
  error: any
  isPreview: boolean
}

function NutritionRecipe({
  data: initialData,
  isLoading: initialIsLoading,
  error: initialError,
  isPreview,
}: Props) {
  const [data, setData] = useState<NutritionRecipe_nutritionRecipe | null>(
    initialData,
  )
  const [isLoading, setIsLoading] = useState(initialIsLoading)
  const [error, setError] = useState(initialError)
  const { country, language } = useLocale()
  const params = useParams()
  const location = useLocation()

  useEffect(() => {
    if (initialData) setData(initialData)
    if (typeof initialIsLoading !== 'undefined') setIsLoading(initialIsLoading)
    if (initialError) setError(initialError)
  }, [initialData, initialIsLoading, initialError])

  useEffect(() => {
    if (data && data.slug !== params.slug) {
      setIsLoading(true)
      NutritionRecipe.getInitialProps({
        params,
        language,
        location,
        isPreview,
      }).then((newData) => {
        if (!newData) return setError(true)
        setData(newData)
        setIsLoading(false)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, params])

  if (error) {
    return <ErrorPage error={error} />
  }

  if (isLoading || !data)
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner width="50" color="green" />
      </div>
    )

  const {
    recipeName: name,
    slug,
    image,
    intro,
    time,
    calories,
    servings,
    instructions,
    ingredients,
    categories,
    id,
    meta,
  } = data

  type FormattedIngredientType = {
    ingredient?: string | null
    ingredientMetric?: string | null
    ingredientName?: string | null
    ingredientUs?: string | null
  }

  const formattedIngredient = ({
    ingredientMetric,
    ingredientName,
    ingredientUs,
    ingredient: ingredientLegacy,
  }: FormattedIngredientType) => {
    const amount =
      country === 'US' && ingredientUs?.length ? ingredientUs : ingredientMetric

    if (!amount) return ingredientLegacy
    return `${amount} ${ingredientName}`
  }

  return (
    <>
      <DatoMeta
        meta={meta}
        fallbackDescription={intro ? intro : undefined}
        canonicalUrl={`https://lifesum.com/nutrition-explained/recipe/${slug}`}
      />
      {language === 'en' ? (
        <NutritionHeader />
      ) : (
        <div className="bg-BgSub p-16">
          <Logo />
        </div>
      )}
      <div className="bg-Bg pb-80 lg:pb-128">
        <div className="bg-BgSub pb-96 pt-48 lg:pb-128">
          <div className="o-width-limiter o-width-limiter--narrow">
            <h1 className="type-large-fat text-center text-green lg:py-40">
              {name}
            </h1>
            <div className="lg:hidden">
              <RecipeMeta small time={time} calories={calories} />
            </div>
          </div>
        </div>

        <div className="o-width-limiter o-width-limiter--narrow relative z-10">
          <div className="mb-48 lg:mb-64 lg:ml-auto lg:w-3/5 xl:w-2/3">
            <figure className="-mt-48 mb-24 md:-mt-64 md:mb-32 lg:-mt-80 xl:mb-40">
              <DatoImage
                src={image?.responsiveImage?.src as string}
                {...(image && image.responsiveImage)}
                title={image?.responsiveImage?.title || undefined}
                lazy
                className="w-full rounded-8 md:rounded-12 lg:rounded-20"
              />
            </figure>
            {intro && <p className="type-med-minus s-text">{intro}</p>}
          </div>
          <div
            className={`
            left-0 top-0
            mb-64 lg:absolute lg:mt-128
            lg:w-2/5 lg:pl-64 lg:pr-40 xl:w-1/3 xl:pr-64
          `}
          >
            <div className="">
              <div className="-mr-40 hidden lg:block">
                <RecipeMeta time={time} calories={calories} />
              </div>
            </div>
            <h2 className="type-upper-fat lg:mt-40">
              <FormattedMessage
                id="recipes_ingredients"
                defaultMessage="Ingredients"
              />
            </h2>
            {servings && (
              <p className="type-small mt-2 text-grey">
                <FormattedMessage
                  id="recipes_servings"
                  defaultMessage="{value} servings"
                  values={{ value: servings }}
                />
              </p>
            )}
            <ul className="mt-24 pl-16">
              {ingredients.map((ingredient) => {
                return (
                  <li
                    className="mb-12 list-disc leading-body text-Brand"
                    key={ingredient.id}
                  >
                    <div className="text-Type">
                      {formattedIngredient(ingredient)}
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
          <div className="mb-64 lg:ml-auto lg:w-3/5 xl:w-2/3">
            <h2 className="type-upper-fat mb-24">
              <FormattedMessage
                id="recipes_instructions"
                defaultMessage="Instructions"
              />
            </h2>
            <ol>
              {instructions.map(({ instruction, id }, i) => {
                if (!instruction) {
                  throw new Error(
                    `typeguard: instruction was null instruction-id: ${id}`,
                  )
                }
                return <Instruction key={id} text={instruction} index={i} />
              })}
            </ol>
            <Categories categories={categories || []} />
          </div>
        </div>
        {language === 'en' && (
          <div className="o-width-limiter my-64 lg:mb-128">
            <div className="mx-auto max-w-1280">
              <div className="mb-48">
                <ShareButtons title={name} />
              </div>
              <SymbolHeading
                symbol={SaucePanHot}
                symbolStyle={{ margin: '-2 0 0 3px' }}
              >
                More recipes
              </SymbolHeading>
              <RelatedStories category="recipes" not={id} />
            </div>
          </div>
        )}
        {language === 'en' && <AppPromo />}
      </div>
      {language === 'en' ? (
        <NutritionFooter />
      ) : (
        <div className="pb-72 pt-80 text-center">
          <Logo />
        </div>
      )}
    </>
  )
}

type InstructionProps = {
  text: string
  index: number
}

function Instruction({ text, index }: InstructionProps) {
  return (
    <li className="mb-12 flex leading-body lg:mb-16 xl:mb-24">
      <div
        className={`
          mr-8 flex 
          shrink-0 rounded-full border-1 border-Brand 
          text-Brand lg:mr-16
        `}
        aria-hidden="true"
        style={{
          width: '1.5em',
          height: '1.5em',
        }}
      >
        <span className="relative m-auto" style={{ lineHeight: 1.5 }}>
          {index + 1}
        </span>
      </div>
      {convert(text)}
    </li>
  )
}

NutritionRecipe.getInitialProps = ({
  params,
  location,
  language,
  isPreview,
}) => {
  const locale = location.search.match(/preview/) ? datoLocales[language] : 'en'

  return runQuery(query, { slug: params.slug, locale }, isPreview).then(
    ({ nutritionRecipe }) => nutritionRecipe,
  )
}

export default withSSR(NutritionRecipe)
